import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { validate as isUuid } from "uuid";

import { hasDataArray } from "@/api/connection.types";
import LabAssetsAPI from "@/api/lab-assets";
import type {
  AssetActivityLogResult,
  ConnectionFailurePayload,
  LabAssetMeta,
} from "@/api/lab-assets/lab-assets.types";
import { queryClient } from "@/index";
import { toast } from "@/utils/toastService";

import type {
  LabAsset,
  LabAssetType,
  UpdateLabAssetBody,
} from "./labAssets.types";
import { labAssetQueryKeys } from "./labAssets.queryKeys";

export { labAssetQueryKeys };

// Matches mfui's default per-cacheKey staleTime for filter options, so both
// cache layers expire together.
const ASSET_META_STALE_TIME = 5 * 60 * 1000;

const isLabAssetMeta = (response: unknown): response is LabAssetMeta =>
  !!response &&
  typeof response === "object" &&
  !("success" in response && response.success === false) &&
  Array.isArray((response as LabAssetMeta).vendors);

/**
 * `GET /assets/meta` for one asset type through the shared queryClient, so the
 * filter-option lists live in the same cache as the rest of the feature:
 * refreshable through `invalidateQueries(labAssetQueryKeys.all)`, cleared by
 * `queryClient.clear()` on logout, and concurrent callers share one in-flight
 * request per key. That logout clear only reaches this layer: mfui's
 * MultiSelectEditor keeps its own module-level per-cacheKey cache of the
 * resolved items, which nothing here clears, so the dropdown itself can still
 * show the previous session's lists until mfui's 5-minute staleTime lapses
 * (same as every other cacheKey filter def in the app).
 *
 * connection.js resolves failures as `{ success: false, error }` instead of
 * rejecting, so the queryFn re-derives the outcome and throws. react-query
 * then stores no data under the key and the next dropdown open retries,
 * rather than serving empty option lists until the staleTime lapses.
 */
export const fetchLabAssetMeta = (assetType: LabAssetType) =>
  queryClient.fetchQuery({
    queryKey: labAssetQueryKeys.meta(assetType),
    queryFn: async (): Promise<LabAssetMeta> => {
      const response = await LabAssetsAPI.getAssetMeta({
        asset_type: assetType,
      });
      if (isLabAssetMeta(response)) return response;
      throw new Error(`Failed to load ${assetType} asset metadata`);
    },
    staleTime: ASSET_META_STALE_TIME,
    // The connection.js interceptor already toasts each failed attempt.
    retry: false,
  });

const GENERIC_LOAD_ERROR =
  "An error occurred while loading this asset. Please try again.";

export type UseLabAssetItemOptions = {
  /**
   * Seed for an instant paint (the flyout passes its table row). Pair with
   * `initialDataUpdatedAt: 0` so the seed counts as stale and the refetch on
   * mount still happens under any staleTime.
   */
  initialData?: LabAsset;
  initialDataUpdatedAt?: number;
};

/**
 * The single owner of the `labAssetQueryKeys.item(uuid)` cache entry — every
 * surface (details page, flyout) must read the asset through this hook so the
 * key has exactly one queryFn and one not-found policy.
 *
 * Fetches one asset via GET /assets/:uuid. connection.js resolves failures
 * (a 404 included) as { success: false, error } instead of rejecting, so the
 * queryFn re-derives the outcome: a real record passes through, a true 404
 * maps to null (the consumers' not-found signal), and every other failure
 * (500, network, revoked permission) throws so it surfaces as isError instead
 * of being cached as a not-found.
 *
 * A malformed uuid (a truncated or hand-edited link) resolves to null without
 * a request: the API validates `:uuid` with z.string().uuid() and answers 400,
 * which would otherwise render as a retryable error that can never succeed.
 */
export const useLabAssetItem = (
  uuid: string | undefined,
  options: UseLabAssetItemOptions = {}
) =>
  useQuery({
    queryKey: labAssetQueryKeys.item(uuid),
    queryFn: async (): Promise<LabAsset | null> => {
      if (!uuid || !isUuid(uuid)) return null;
      // Opts out of the interceptor toast: a 404 is rendered by the page's
      // not-found state (and a toast here would stack on the edit modal's
      // own "Asset not found" when a failed save refetches the item), so
      // only the retryable failures below toast, once per attempt. A 401
      // stays silent for the same reason the interceptor skips it:
      // connection.js handleError is already reloading out to login.
      const response = (await LabAssetsAPI.getAssetItem(uuid, {
        skipErrorToast: true,
      })) as LabAsset | ConnectionFailurePayload | undefined;
      if (response && "uuid" in response && response.uuid) {
        return response;
      }
      const failure =
        response && "error" in response ? response.error?.response : undefined;
      if (failure?.status === 404) return null;
      if (failure?.status !== 401) {
        toast.error(failure?.data?.message || GENERIC_LOAD_ERROR);
      }
      throw new Error(`Failed to load asset ${uuid}`);
    },
    // No auto-retry (mirrors storage.query.ts); the page offers a manual
    // retry via refetch, and each attempt toasts once above.
    retry: false,
    enabled: !!uuid,
    ...options,
  });

const HISTORY_PAGE_SIZE = 50;

/**
 * POST /assets/:uuid/activity-log, newest first, one page per
 * `next_page`. Failures resolve as { success: false } via connection.js, so
 * the queryFn throws to surface them as isError (the interceptor already
 * toasted); no auto-retry, the feed offers a manual one.
 */
export const useAssetActivityLog = (uuid: string) =>
  useInfiniteQuery({
    queryKey: labAssetQueryKeys.activityLog(uuid),
    queryFn: async ({ pageParam }): Promise<AssetActivityLogResult> => {
      const response = await LabAssetsAPI.getAssetActivityLog(uuid, {
        query: { page: pageParam, pageSize: HISTORY_PAGE_SIZE },
      });
      if (hasDataArray(response)) return response;
      throw new Error(`Failed to load activity log for asset ${uuid}`);
    },
    getNextPageParam: (lastPage) => lastPage.next_page,
    initialPageParam: 1,
    retry: false,
  });

/** `200 { message, uuid, updated_fields }` from PATCH /assets/:uuid — no row. */
export type UpdateLabAssetResponse = {
  message: string;
  uuid: string;
  updated_fields: string[];
};

export type UpdateLabAssetVariables = {
  uuid: string;
  body: UpdateLabAssetBody;
};

const GENERIC_UPDATE_ERROR =
  "An error occurred while updating this asset. Please try again.";

/**
 * Thrown by useUpdateLabAsset for a failed PATCH. `status` is the HTTP
 * status when the server answered (undefined for a network failure) so
 * onError can tell a 404 from a dropped connection.
 */
export class LabAssetUpdateError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = "LabAssetUpdateError";
    this.status = status;
  }
}

/**
 * PATCH /assets/:uuid. Modeled on useUpdateTask (tasks.query.ts).
 *
 * connection.js resolves HTTP failures as { success: false, error } instead
 * of rejecting, so the mutationFn re-throws them as LabAssetUpdateError (with
 * the server's message when it sends one — a 400 validation error, a 403, or
 * a 404 for an asset deleted mid-edit) so they surface through onError/isError
 * rather than as a success. The request opts out of the interceptor toast; the
 * caller owns the single error snackbar.
 *
 * The response carries no row, so onSuccess invalidates the item cache (the
 * details page and flyout refetch through useLabAssetItem), the infinite
 * lists (the table refetches on its next mount) and the asset's activity log
 * (the History tab picks up the new rows while open). On failure only a 404
 * invalidates the item: the record on screen is gone, and the refetch lets
 * useLabAssetItem render not-found (silently — it maps a 404 to null) instead
 * of leaving the old row up. Every other failure leaves the cache alone, since
 * a refetch would toast a second time (a dropped connection would fail the
 * GET too) on top of the caller's save error.
 */
export const useUpdateLabAsset = (options?: {
  onSuccess?: (data: UpdateLabAssetResponse) => void;
  onError?: (error: LabAssetUpdateError) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateLabAssetResponse,
    LabAssetUpdateError,
    UpdateLabAssetVariables
  >({
    mutationFn: async ({ uuid, body }) => {
      const result = (await LabAssetsAPI.updateAssetItem(uuid, body, {
        skipErrorToast: true,
      })) as UpdateLabAssetResponse | ConnectionFailurePayload | undefined;
      // A success payload has no `success` key; only the failure envelope
      // does (always `success: false`).
      if (!result || "success" in result) {
        const failure = result?.error?.response;
        throw new LabAssetUpdateError(
          failure?.data?.message || GENERIC_UPDATE_ERROR,
          failure?.status
        );
      }
      return result;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: labAssetQueryKeys.item(variables.uuid),
      });
      queryClient.invalidateQueries({
        queryKey: labAssetQueryKeys.infiniteLists,
      });
      queryClient.invalidateQueries({
        queryKey: labAssetQueryKeys.activityLog(variables.uuid),
      });
      options?.onSuccess?.(data);
    },
    onError: (error, variables) => {
      if (error.status === 404) {
        queryClient.invalidateQueries({
          queryKey: labAssetQueryKeys.item(variables.uuid),
        });
      }
      options?.onError?.(error);
    },
  });
};
